/**
 * Mapeo de errores de Postgrest/Supabase a mensajes legibles para el usuario final.
 *
 * Dos APIs:
 *  - `toUserMessage(e)` — sync, fallback rápido (no toca BD).
 *  - `enrichConstraintMessage(e, supabase)` — async, para los call-sites
 *    que se beneficien con info del row colisionante (ej: nombre del
 *    edificio existente). Se usa en mutations de tablas con constraints
 *    documentados en `CONSTRAINT_LOOKUPS`. Si la query falla o no hay
 *    config, cae al sync `toUserMessage`.
 *
 * Para errores de UNIQUE constraint (`23505`):
 *   1. Extraemos el nombre del constraint del `error.message` crudo.
 *   2. Si está en `CONSTRAINT_LOOKUPS` (mapa enrichment async) y nos
 *      pasaron el cliente supabase, parseamos `error.details` para
 *      obtener los valores en conflicto y consultamos el row existente
 *      para incluir su nombre/código.
 *   3. Si no se enriqueció, caemos al mapa sync `UNIQUE_CONSTRAINT_MESSAGES`.
 *   4. Si tampoco hay match, fallback genérico.
 *
 * TODO follow-up:
 *  - 23503 (FK): podríamos resolver el nombre de la tabla referenciada
 *    para decir "este edificio tiene comprobantes; no lo podés borrar".
 *  - 23502 (NOT NULL): identificar la columna y traducir a label de UI.
 *
 * Patrón aplicable: P-API-05 (mensajes de error consistentes).
 * Trazabilidad: TODO-UX-01 en `knowledge-base/DECISIONES.md`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export class AppError extends Error {
  constructor(
    public code: string,
    message: string,
    public context?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export interface PostgrestLike {
  code?: string;
  message?: string;
  details?: string | null;
  hint?: string | null;
}

export function isPostgrestError(e: unknown): e is PostgrestLike {
  return typeof e === 'object' && e !== null && ('code' in e || 'message' in e);
}

// ─────────────────────────────────────────────────────────────────────────
// Mensajes sync (fallback rápido sin BD)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Mapa constraint → función sync que arma el mensaje.
 * Recibe `details` por si quiere extraer valores del row.
 */
const UNIQUE_CONSTRAINT_MESSAGES: Record<
  string,
  (details: string | null | undefined) => string
> = {
  // Edificios
  uq_edificios_administracion_codigo: (d) => {
    const vals = parseDetailsValues(d);
    const codigo = vals.codigo;
    return codigo
      ? `Ya existe un edificio con código ${codigo} en esta administración. Probá con otro código o revisá la grilla con los filtros activos.`
      : 'Ya existe un edificio con ese código en esta administración.';
  },

  // Administraciones
  administraciones_codigo_empresa_unique: () =>
    'Ya existe una administración con ese código en esta empresa. Usá otro código.',
  uq_administraciones_codigo_por_empresa: () =>
    'Ya existe una administración con ese código en esta empresa. Usá otro código.',

  // Emails de administración
  administracion_emails_uq_email_por_admin: () =>
    'Este email ya está cargado para esta administración.',
  administracion_emails_uq_principal: () =>
    'No se puede tener más de un email principal por administración.',

  // Cajas
  cajas_empresa_nombre_unique: () => 'Ya existe una caja con ese nombre en esta empresa.',

  // Categorías
  categorias_finanzas_empresa_nombre_tipo_unique: () =>
    'Ya existe una categoría con ese nombre y tipo en esta empresa.',

  // Comprobantes (numerador único por empresa+tipo+pv)
  comprobantes_empresa_tipo_pv_numero_unique: () =>
    'Ya existe un comprobante con ese tipo, punto de venta y número.',

  // Profiles
  profiles_email_key: () => 'Ya hay un usuario con ese email.',
  profiles_pkey: () => 'Ya existe un usuario con esos datos.',
};

// ─────────────────────────────────────────────────────────────────────────
// Lookup async (trae el row en conflicto desde la BD)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Configuración de cómo enriquecer un mensaje consultando el row
 * colisionante. La función recibe los valores parseados de `details`
 * y el cliente supabase, y devuelve el mensaje final (o `null` si no
 * pudo enriquecer; en ese caso se usa el mapa sync).
 */
type Enricher = (
  values: Record<string, string>,
  supabase: SupabaseClient,
) => Promise<string | null>;

const CONSTRAINT_LOOKUPS: Record<string, Enricher> = {
  // Edificios — busca el edificio existente por (administracion_id, codigo)
  uq_edificios_administracion_codigo: async (vals, supabase) => {
    const { administracion_id, codigo } = vals;
    if (!administracion_id || !codigo) return null;
    const { data } = await supabase
      .from('edificios')
      .select('nombre, activo')
      .eq('administracion_id', administracion_id)
      .eq('codigo', codigo)
      .maybeSingle();
    if (!data?.nombre) return null;
    const inactivo = data.activo === false ? ' (inactivo)' : '';
    return `Ya existe el edificio "${data.nombre}"${inactivo} con código ${codigo} en esta administración. Si es el mismo edificio, editalo desde la grilla; si es distinto, usá otro código.`;
  },

  // Emails de admin — el email duplicado lo sabemos de los values, pero
  // podemos enriquecer con el nombre/rol que tenía la fila previa.
  // El constraint usa `lower(email)`, parseDetailsValues normaliza la
  // columna a `email` (ver normalizeColumnExpr).
  administracion_emails_uq_email_por_admin: async (vals, supabase) => {
    const { administracion_id, email } = vals;
    if (!administracion_id || !email) return null;
    const { data } = await supabase
      .from('administracion_emails')
      .select('email, nombre, rol, es_principal, activo')
      .eq('administracion_id', administracion_id)
      .ilike('email', email)
      .maybeSingle();
    if (!data?.email) return null;
    const detalle = [
      data.es_principal ? 'principal' : null,
      data.rol ? `rol: ${data.rol}` : null,
      data.activo === false ? 'inactivo' : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const tail = detalle ? ` (${detalle})` : '';
    return `El email "${data.email}"${tail} ya está cargado para esta administración. Editalo desde la lista de emails si querés cambiar el rol o reactivarlo.`;
  },

  // Administraciones — buscar admin existente por (empresa_id, codigo)
  administraciones_codigo_empresa_unique: async (vals, supabase) =>
    enrichAdministracion(vals, supabase),
  uq_administraciones_codigo_por_empresa: async (vals, supabase) =>
    enrichAdministracion(vals, supabase),
};

async function enrichAdministracion(
  vals: Record<string, string>,
  supabase: SupabaseClient,
): Promise<string | null> {
  const { empresa_id, codigo } = vals;
  if (!empresa_id || !codigo) return null;
  const { data } = await supabase
    .from('administraciones')
    .select('nombre, activo')
    .eq('empresa_id', empresa_id)
    .eq('codigo', codigo)
    .maybeSingle();
  if (!data?.nombre) return null;
  const inactivo = data.activo === false ? ' (inactiva)' : '';
  return `Ya existe la administración "${data.nombre}"${inactivo} con código ${codigo} en esta empresa. Usá otro código o editá la existente.`;
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers de parsing
// ─────────────────────────────────────────────────────────────────────────

/**
 * Parsea el `details` típico de Postgres a un objeto col → valor.
 *   "Key (col1, col2)=(v1, v2) already exists." → { col1: 'v1', col2: 'v2' }
 *
 * Soporta paréntesis anidados en columnas (ej: `Key (a, lower(email))=(...)`)
 * usando balance de paréntesis en lugar de regex naive.
 *
 * Para columnas tipo `lower(email)`, normaliza a `email` para que el
 * lookup funcione.
 */
function parseDetailsValues(details?: string | null): Record<string, string> {
  if (!details) return {};
  const start = details.indexOf('Key (');
  if (start < 0) return {};
  const colsRaw = readBalanced(details, start + 'Key '.length);
  if (!colsRaw) return {};
  const eqIdx = details.indexOf('=(', start + colsRaw.endIndex);
  if (eqIdx < 0) return {};
  const valsRaw = readBalanced(details, eqIdx + 1);
  if (!valsRaw) return {};

  const cols = splitTopLevel(colsRaw.body).map(normalizeColumnExpr);
  const vals = splitTopLevel(valsRaw.body);
  const out: Record<string, string> = {};
  for (let i = 0; i < cols.length; i++) {
    const k = cols[i];
    if (k) out[k] = vals[i] ?? '';
  }
  return out;
}

/** Lee desde `(` el contenido balanceado y devuelve el body + endIndex. */
function readBalanced(
  s: string,
  fromIdx: number,
): { body: string; endIndex: number } | null {
  if (s[fromIdx] !== '(') return null;
  let depth = 1;
  for (let i = fromIdx + 1; i < s.length; i++) {
    if (s[i] === '(') depth++;
    else if (s[i] === ')') {
      depth--;
      if (depth === 0) return { body: s.slice(fromIdx + 1, i), endIndex: i + 1 };
    }
  }
  return null;
}

/** Split por comas en el nivel top — ignora comas dentro de `(...)`. */
function splitTopLevel(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of s) {
    if (ch === '(') {
      depth++;
      cur += ch;
    } else if (ch === ')') {
      depth--;
      cur += ch;
    } else if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

/**
 * Si la columna es una expresión `funcion(arg)` (ej: `lower(email)`),
 * usamos el `arg` como key del map. Esto permite que el lookup async
 * que busca por `email` matchee aunque el constraint esté definido
 * sobre `lower(email)`.
 */
function normalizeColumnExpr(c: string): string {
  const m = c.match(/^[a-z_]+\(([^)]+)\)$/i);
  if (m) return m[1]!.trim();
  return c;
}

function extractConstraintName(message?: string | null): string | null {
  if (!message) return null;
  const m = message.match(/unique constraint "([^"]+)"/);
  return m?.[1] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────
// API pública
// ─────────────────────────────────────────────────────────────────────────

export function toUserMessage(e: unknown): string {
  if (isPostgrestError(e)) {
    switch (e.code) {
      case '23505': {
        const constraint = extractConstraintName(e.message);
        if (constraint && UNIQUE_CONSTRAINT_MESSAGES[constraint]) {
          return UNIQUE_CONSTRAINT_MESSAGES[constraint](e.details ?? null);
        }
        return 'Ya existe un registro con esos datos.';
      }
      case '23503':
        return 'No se puede completar la operación: hay datos relacionados.';
      case '23514':
        return 'Uno de los valores enviados no es válido.';
      case '23502':
        return 'Falta completar un campo obligatorio.';
      case '42501':
        return 'No tenés permisos para esta acción.';
      case '42703':
        return 'Hay un campo mal identificado. Avisale al equipo técnico.';
      case 'PGRST116':
        return 'No se encontró el registro.';
      case 'PGRST301':
        return 'Tu sesión expiró. Volvé a iniciar sesión.';
      default:
        return e.message || 'Ocurrió un error inesperado.';
    }
  }
  if (e instanceof Error) return e.message;
  return 'Ocurrió un error inesperado.';
}

/**
 * TODO-MDC-07: extracción de error real desde una respuesta de
 * `supabase.functions.invoke()`. Sin esto, todos los errores de edge fn
 * aparecen como genéricos "Edge Function returned a non-2xx status code"
 * — que es inservible para diagnóstico.
 *
 * Supabase JS adjunta el body real del error en `error.context`:
 *   { context: { error: '...', message: '...' } }
 *
 * Uso:
 *   const { data, error } = await supabase.functions.invoke('arca-...', {...});
 *   if (error) toast.error(extractFunctionError(error));
 */
export function extractFunctionError(e: unknown): string {
  if (!e) return 'Error desconocido';
  const obj = e as {
    context?: { error?: string | { code?: string; message?: string }; message?: string };
    message?: string;
  };
  const ctx = obj?.context;
  if (ctx) {
    if (typeof ctx.error === 'string' && ctx.error.length > 0) return ctx.error;
    if (typeof ctx.error === 'object' && ctx.error?.message) return ctx.error.message;
    if (ctx.message) return ctx.message;
  }
  if (obj.message) return obj.message;
  return String(e);
}

/**
 * Versión async: si es un 23505 con constraint conocido, intenta traer
 * el row colisionante para incluir su nombre/detalle. Si no, cae al
 * sync `toUserMessage`.
 *
 * Uso típico desde un service:
 *   if (error) return err(error.code, await enrichConstraintMessage(error, supabase));
 */
export async function enrichConstraintMessage(
  e: unknown,
  supabase: SupabaseClient,
): Promise<string> {
  if (!isPostgrestError(e) || e.code !== '23505') return toUserMessage(e);
  const constraint = extractConstraintName(e.message);
  if (!constraint) return toUserMessage(e);
  const lookup = CONSTRAINT_LOOKUPS[constraint];
  if (lookup) {
    try {
      const values = parseDetailsValues(e.details);
      const enriched = await lookup(values, supabase);
      if (enriched) return enriched;
    } catch {
      // Si la query falla por permisos/red/etc, fallback al sync.
    }
  }
  return toUserMessage(e);
}
